import React, { useCallback, useEffect, useState } from 'react';
import { Button, Group, Icon } from '@tlx/atlas';
import { useIntl } from 'react-intl';
import { useDebouncedQuery } from '@Hooks/useDebouncedQuery.ts';

type IconVariant = 'chevron' | 'arrow' | 'arrow2' | 'ios';

type YearSelectProps = {
    options?: string[];
    initial?: string;
    variant?: IconVariant;
    setYear?: React.Dispatch<React.SetStateAction<number>>;
    className?: string;
};

export function YearSelect({ options, initial, variant, setYear, className }: YearSelectProps) {
    const [selectedYear, setSelectedYear] = useState(initial);
    const { currentIndex, priorDisabled, nextDisabled } = getState(options, selectedYear);

    const intl = useIntl();

    const setIndex = useCallback(
        (offset: number) => {
            if (
                options !== undefined &&
                currentIndex !== undefined &&
                currentIndex + offset >= 0 &&
                currentIndex + offset <= options.length - 1
            ) {
                setSelectedYear(options[currentIndex + offset]);
            }
        },
        [currentIndex, options],
    );

    const goToPrior = useCallback(() => {
        setIndex(-1);
    }, [setIndex]);

    const goToNext = useCallback(() => {
        setIndex(+1);
    }, [setIndex]);

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if ((event.target as HTMLElement).tagName === 'INPUT') {
                return;
            }
            if (event.key === 'ArrowLeft') {
                goToPrior();
            } else if (event.key === 'ArrowRight') {
                goToNext();
            }
        };

        document.addEventListener('keydown', handleKeyDown);

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [goToNext, goToPrior]);

    //Only sends a dispatch (to change the page) after 250ms so it only changes once you are finished picking the year
    const debounced = useDebouncedQuery(selectedYear ?? '', 300);
    useEffect(() => {
        if (!isEmpty(debounced)) {
            setYear?.(+debounced);
        }
    }, [debounced, setYear]);

    const { priorIcon, nextIcon } = getIcons(variant);

    return (
        <Group className={`${className ?? ''} atl-flex yearSelect atl-rounded`}>
            <div className="atl-text-base atl-ml-16 atl-mr-8">{selectedYear}</div>
            <Button
                className="atl-date-input__button"
                variant="icon"
                disabled={priorDisabled}
                onClick={goToPrior}
                data-testid="tax-card-previous-year"
                aria-label={intl.formatMessage({ id: 'text_previous_year' })}
            >
                <Icon>{priorIcon}</Icon>
            </Button>
            <Button
                className="atl-date-input__button"
                variant="icon"
                disabled={nextDisabled}
                onClick={goToNext}
                data-testid="tax-card-next-year"
                aria-label={intl.formatMessage({ id: 'text_next_year' })}
            >
                <Icon>{nextIcon}</Icon>
            </Button>
        </Group>
    );
}

function isEmpty(text: string) {
    return text === null || text === undefined || text.length === 0;
}

function getState(
    options?: string[],
    selected?: string,
): {
    currentIndex?: number;
    priorDisabled: boolean;
    nextDisabled: boolean;
} {
    if (options === undefined || options.length === 0 || selected === undefined) {
        return {
            priorDisabled: true,
            nextDisabled: true,
        };
    }

    const currentIndex = options.findIndex((option) => option === selected);

    const priorDisabled = currentIndex <= 0;
    const nextDisabled = currentIndex >= options.length - 1;

    return {
        currentIndex,
        priorDisabled,
        nextDisabled,
    };
}

function getIcons(variant?: IconVariant): {
    priorIcon: string;
    nextIcon: string;
} {
    switch (variant) {
        case 'arrow':
            return {
                priorIcon: 'arrow_left',
                nextIcon: 'arrow_right',
            };

        case 'arrow2':
            return {
                priorIcon: 'arrow_back',
                nextIcon: 'arrow_forward',
            };

        case 'chevron':
            return {
                priorIcon: 'chevron_left',
                nextIcon: 'chevron_right',
            };

        case 'ios':
            return {
                priorIcon: 'arrow_back_ios',
                nextIcon: 'arrow_forward_ios',
            };
        default:
            return getIcons('arrow');
    }
}
